import axios from 'axios';
import { ExportUserError } from './helpers';

export const EXPORT_POLL_INTERVAL_MS = 2000;
export const EXPORT_POLL_TIMEOUT_MS = 60000;

export type SelectedDateRange = {
  start: Date;
  end: Date;
};

export type StartExportFn = () => Promise<string>;

export const pollExportJob = async (
  url: string,
  signal?: AbortSignal,
  pollIntervalMs: number = EXPORT_POLL_INTERVAL_MS,
  pollTimeoutMs: number = EXPORT_POLL_TIMEOUT_MS
): Promise<void> => {
  const start = Date.now();

  const checkGcsForExport = async (): Promise<boolean> => {
    const response = await axios.head(url, {
      responseType: 'blob',
      validateStatus: status => status === 200 || status === 404,
    });

    return response.status === 200;
  };

  while (Date.now() - start < pollTimeoutMs) {
    if (signal?.aborted) {
      throw new ExportUserError('Export canceled');
    }

    const ready = await checkGcsForExport();
    if (ready) return;

    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  throw new ExportUserError('Export timed out');
};

export const downloadExport = async (downloadUrl: string, filename: string): Promise<void> => {
  const response = await axios.get(downloadUrl, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data]);
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(href);
};

export interface RunExportParams {
  startExport: StartExportFn;
  filename?: string;
  signal?: AbortSignal;
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
}

export const runDonationExportFlow = async ({
  startExport,
  filename = 'oath_donation_export.csv',
  signal,
  pollIntervalMs = EXPORT_POLL_INTERVAL_MS,
  pollTimeoutMs = EXPORT_POLL_TIMEOUT_MS,
}: RunExportParams): Promise<void> => {
  const signedDownloadUrl = await startExport();
  await pollExportJob(signedDownloadUrl, signal, pollIntervalMs, pollTimeoutMs);
  await downloadExport(signedDownloadUrl, filename);
};
